// src/components/SubmitTime/components/SubmitTimeModal.tsx
// Modal wrapper using the same RenderInBody + CorgeeModal pattern as the rest of the app

import React, { useState, useRef, useEffect } from 'react';
import RenderInBody from '../../Globals/RenderInBody';
import { SubmitTimeSkeleton } from './SubmitTimeSkeleton';
import '../../Globals/CorgeeModal.css';

interface SubmitTimeModalProps {
  show: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
  isEdit: boolean;
  isSaving?: boolean;
  canDelete?: boolean;
  isInitializing?: boolean;
  isProcessing?: boolean;
  submitLabel?: string;
  showSaveAndNew?: boolean;
  /** Notifies the parent when the "Save and New" checkbox changes */
  onSaveAndNewChange?: (checked: boolean) => void;
  children: React.ReactNode;
}

export const SubmitTimeModal: React.FC<SubmitTimeModalProps> = ({
  show,
  title,
  onClose,
  onSave,
  onDelete,
  isEdit,
  isSaving = false,
  canDelete = false,
  isInitializing = false,
  isProcessing = false,
  submitLabel = 'Submit',
  showSaveAndNew,
  onSaveAndNewChange,
  children,
}) =>
{
  // showSaveAndNew defaults to true only for new entries (not edit)
  const displaySaveAndNew = showSaveAndNew ?? !isEdit;
  const [saveAndNew, setSaveAndNew] = useState(false);
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Reset position and the "Save and New" flag when the modal closes
  useEffect(() =>
  {
    if (!show)
    {
      setPosition(null);
      setSaveAndNew(false);
      onSaveAndNewChange?.(false);
    }
  }, [show]);

  useEffect(() =>
  {
    const onMouseMove = (e: MouseEvent) =>
    {
      if (!dragging.current) return;
      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };
    const onMouseUp = () =>
    {
      if (dragging.current)
      {
        dragging.current = false;
        setIsDragging(false);
      }
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () =>
    {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleHeaderMouseDown = (e: React.MouseEvent) =>
  {
    if (!modalRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = modalRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    dragging.current = true;
    setIsDragging(true);
  };

  if (!show) return null;

  const modalStyle: React.CSSProperties = position
    ? { position: 'fixed', left: position.x, top: position.y, transform: 'none', margin: 0 }
    : {};

  return (
    <RenderInBody>
      <div className="corgee_com_modal-backdrop submit-time-backdrop-portal">
        <div
          ref={modalRef}
          className="corgee_com_conversation-metadata-modal submit-time-dialog dialogTime"
          style={modalStyle}
          onPointerDown={e => e.stopPropagation()}
        >
          {/* Processing overlay — blocks all interaction while request is in flight */}
          {isProcessing && (
            <div className="submit-time-processing-overlay">
              <div className="submit-time-processing-spinner" />
              <div className="submit-time-processing-text">
                Please do not close this window,
                <br />
                your request is being processed.
              </div>
            </div>
          )}

          {/* Header — drag handle */}
          <div
            className="corgee_com_global-modal_header"
            onMouseDown={handleHeaderMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}
          >
            <div className="header-info">
              <div className="react-add-new-card-title">{title}</div>
              <button
                className="corgee_com_global-modal_header-close-btn"
                onClick={onClose}
                type="button"
                onMouseDown={e => e.stopPropagation()}
              >
                <i className="icon-incorrect-icon"></i>
              </button>
            </div>
          </div>

          {/* Scrollable area: body + footer scroll together; header stays fixed */}
          <div className="submit-time-scroll-area">
            {/* Body */}
            <div className="corgee_com_global-modal_body modal_body-max-height">
              {isInitializing ? <SubmitTimeSkeleton /> : children}
            </div>

            {/* Footer */}
            <div className="corgee_com_global-modal_footer">
              <div>
                {displaySaveAndNew && (
                  <div className="corgee_com_global-modal_save-and-new">
                    <label
                      className="corgee_com_global-modal_save-and-new-title"
                      htmlFor="createAnotherTime"
                    >
                      Save and New
                    </label>
                    <input
                      type="checkbox"
                      id="createAnotherTime"
                      checked={saveAndNew}
                      onChange={e =>
                      {
                        setSaveAndNew(e.target.checked);
                        onSaveAndNewChange?.(e.target.checked);
                      }}
                    />
                  </div>
                )}

                {isEdit && canDelete && (
                  <button
                    type="button"
                    className="corgee-buttonSubmitTimet delete"
                    onClick={onDelete}
                    disabled={isSaving}
                  >
                    Delete
                  </button>
                )}

                <button
                  type="button"
                  className="corgee-buttonSubmitTimet secondarySubmitTimet"
                  onClick={onClose}
                  disabled={isSaving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="corgee-buttonSubmitTimet"
                  onClick={onSave}
                  disabled={isSaving}
                >
                  {isSaving ? `${submitLabel}...` : submitLabel}
                </button>
              </div>
            </div>
          </div>
          {/* end submit-time-scroll-area */}
        </div>
      </div>
    </RenderInBody>
  );
};

export default SubmitTimeModal;
